import { linestringsToPolylines } from '@/mixins/map.js'
import moment from 'moment'
import {
  formatDatetimeWithSeconds,
  datetimeToTimestamp,
  adjustDateWithTimezone,
} from '@/utils/dates.js'
import rightsPlugin from '@/plugins/rights-plugin.js'
import {
  getNestedValue,
  getNestedValueFormattedDatetimeMilliseconds,
  getNestedValueFormattedTime,
} from '@/utils/object.js'
import { generateShortId } from '@/utils/crypto.js'
import { APIV2ResponseDatetimeFormat } from '@/config/simpliciti-apis.js'
import { metersToKilometers } from '@/utils/number'
import { stringDatetimeToDate } from '@/utils/dates.js'
import vehicleService from '@/services/vehicle-service'

export function normalizeCategory(c) {
  return Object.freeze({
    id: getNestedValue(c, 'id'),
    name: getNestedValue(c, 'label'),
    parent_id: getNestedValue(c, 'parentId'),
  })
}

/**
 * Normalizes an API response item from real-time/history by vehicle/driver/circuit (APIV2)
 *
 * @param {*} item
 * @param {*} options
 * @returns
 */
export default function normalizeEntity(item, options = {}) {
  const vehicleId = getNestedValue(item, [
    'vehicule.id',
    'vehicule_id',
    'vehicleId',
    'vehicle.id',
  ])

  const vehicleCategory = options.vehicleCategory || {}
  const vehicleCategoryName =
    getNestedValue(
      item,
      [
        'vehicule.categorie_nom',
        'vehicule_categorie_nom',
        'categorie_nom',
        'vehicleCategoryName',
        'vehicle.category.label',
      ],
      null
    ) ||
    vehicleCategory.name ||
    ''

  const vehicleCategoryClassName = getNestedValue(item, [
    'vehicule.categorie_classe',
    'vehicle.category.class',
  ])

  //options.getNormalizedItem = () => normalizedItem

  // Take linestring raw data from linestring_troncons if available (#35102)
  item.linestring = getNestedValue(
    item,
    [
      'linestring_troncons' /*history by circuit*/,
      'linestring' /*history by vehicle/driver*/,
      'linestrings',
    ],
    '',
    {
      transform(value) {
        if (value instanceof Array) {
          //{couleur:'',linestring:''}
          return value.map((valueItem) => {
            if (typeof valueItem === 'string') {
              //Use case: History by vehicle/driver
              return {
                linestring: valueItem,
                color: null,
              }
            }

            if (valueItem && typeof valueItem === 'object') {
              if (
                valueItem.type === 'LineString' &&
                Array.isArray(valueItem.coordinates)
              ) {
                // Handle APIV3 response : convert coordinates array into a formatted string
                const formattedCoordinates = valueItem.coordinates
                  .map((coordPair) => coordPair.join(' '))
                  .join(',')

                return {
                  linestring: formattedCoordinates,
                  color: getNestedValue(valueItem, 'couleur', null),
                }
              }
            }

            // Handle APIV2 response : get linestring coordinates
            return {
              linestring: getNestedValue(valueItem, 'linestring', ''),
              color: getNestedValue(valueItem, 'couleur', null),
            }
          })
        } else {
          return value
        }
      },
    }
  )

  //A trip history colliding a zone interdiction might produce multiple linestrings/polylines.
  let relatedPolylines = (options.relatedPolylines =
    item.linestring === undefined
      ? []
      : getPolylinesFromItemLinestring(item, {
          ...options,
          color: getNestedValue(item, 'couleur'),
          transform(obj) {
            obj.itemId = vehicleId
            return obj
          },
        }))

  let normalizedItem = {}

  return (normalizedItem = Object.freeze({
    //Common (Mixed)f
    searchType: options.isHistoryMode ? 'history' : 'realtime',
    id: vehicleId,
    type: options.selectionType,
    label: (options.label = getLocationItemLabel(item, options.selectionType)),

    //Temp reel
    speed: (options.speed = getNestedValue(item, 'realTime.speed', '0')),
    statusColor: getNestedValue(item, ['realTime.statusColor', 'couleur'], ''),
    statusLabel: getNestedValue(item, 'realTime.statusLabel', ''),
    address: {
      streetNumber: getNestedValue(item, 'realTime.address.number'),
      streetAddress: getNestedValue(
        item,
        ['realTime.address.street', 'adresse_deb', 'startAddress'],
        ''
      ),
      zipCode: getNestedValue(
        item,
        ['realTime.address.zipcode', 'cp_deb', 'startZipCode'],
        ''
      ),
      city: getNestedValue(
        item,
        ['realTime.address.city', 'ville_deb', 'startCity'],
        ''
      ),
    },
    addressFormatted: (() => {
      let address = {
        streetNumber: getNestedValue(item, 'realTime.address.number'),
        streetAddress: getNestedValue(
          item,
          ['realTime.address.street', 'adresse_deb', 'startAddress'],
          ''
        ),
        zipCode: getNestedValue(
          item,
          ['realTime.address.zipcode', 'cp_deb', 'startZipCode'],
          ''
        ),
        city: getNestedValue(
          item,
          ['realTime.address.city', 'ville_deb', 'startCity'],
          ''
        ),
      }
      let r = `${address.streetNumber} ${address.streetAddress}`.trim()
      r = (r ? r + `, ` : r + ``) + address.zipCode
      r = (r ? r + `, ` : r + ``) + address.city
      return r
    })(),
    lat: getNestedValue(item, 'realTime.latitude'),
    lng: getNestedValue(item, 'realTime.longitude'),
    cap: getNestedValue(item, 'realTime.cap'),
    //Timestamp80
    timestamp80: getNestedValue(item, 'realTime.timestamp80', null),

    //Sensor infos (Temp reel)
    ...parseLocationItemSensors(item),

    //Vehicle (Mixed)
    vehicleId: parseInt(
      getNestedValue(
        item,
        ['vehicule.id', 'vehicle.id', 'vehicule_id', 'vehicleId'],
        null
      )
    ),
    vehicleName: getNestedValue(
      item,
      ['vehicule.nom', 'vehicle.name', 'vehicule_nom', 'vehicleName'],
      null
    ),
    vehicleCategoryClassName,

    vehicleCategoryId: getNestedValue(vehicleCategory, 'id', null),
    vehicleCategoryName,
    vehicleStatusColor: getNestedValue(item, 'realTime.statusColor', ''),
    vehicleIconColor: vehicleService.getVehicleIconColorFromCategory(
      vehicleCategoryClassName
    ),
    vehicleContactOn: getNestedValue(item, 'realTime.contact', false),
    vehicleStatusLabel: getNestedValue(item, 'realTime.statusLabel', ''),
    //Vehicle immatriculation
    vehicleMatriculation: getNestedValue(
      item,
      [
        'vehicule.immatriculation',
        'vehicule_immatriculation',
        'registrationPlate',
        'vehicle.registrationPlate',
      ],
      ''
    ),

    //Circuit (Mixed)
    circuitId: getNestedValue(
      item,
      [
        'circuit.id',
        'circuit_id',
        'executions_circuits.0.circuit_id',
        'roundExecutions.0.roundId',
        'round.id',
      ],
      null
    ),
    circuitExecutions: getNestedValue(
      item,
      ['executions_circuits', 'roundExecutions'],
      [],
      {
        transform(value) {
          return (value || []).map((item) => {
            return {
              executionId: item.executioncircuit_id || item.roundExecutionId,
              circuitId: item.circuit_id || item.roundId,
              startDate: moment(item.start) || moment(item.start),
              endDate: item.end
                ? moment(item.end)
                : moment(item.start).hour(23).minute(59).second(59),
              circuitName: item.nomcourt || item.roundName,
            }
          })
        },
      }
    ),
    circuitName: getNestedValue(
      item,
      ['circuit.nom_court', 'circuit_nom_court', 'round.shortName'],
      null
    ),
    circuitCategoryName: getNestedValue(
      item,
      ['circuit_categorie', 'round.categoryName'],
      ''
    ),
    circuitExecutionId: getNestedValue(
      item,
      ['circuit.execution_id', 'executioncircuit_id', 'round.executionId'],
      null
    ),
    circuitStartDate: getNestedValue(
      item,
      ['circuit.debut_execution', 'round.executionStartAt'],
      null
    ),
    circuitStartDateFormatted: getNestedValueFormattedDatetimeMilliseconds(
      item,
      ['circuit.debut_execution', 'round.executionStartAt']
    ),
    circuitCompletionRate: (() => {
      if (
        rightsPlugin.hasRight(
          'GEOV3_LOCALISATION_HISTO_VIEW_IGNORED_ROUTE_SECTIO'
        )
      ) {
        return getNestedValue(
          item,
          [
            'circuit.taux_realisation',
            'ignored_taux_realisation',
            'round.realizationRateIgnored',
          ],
          0
        )
      }

      return getNestedValue(
        item,
        [
          'circuit.taux_realisation',
          'taux_realisation_circuit',
          'round.realizationRate',
        ],
        0
      )
    })(),
    circuitDriversCount: (() => {
      let team = getNestedValue(
        item,
        ['fdr.equipage.membres', 'planningRoute.crew.realMembers'],
        []
      )
      return team.length
    })(),
    /**
     * Use now as end date if null is supplied
     */
    circuitServiceDuration: (() => {
      let start = moment(
        getNestedValue(
          item,
          ['fdr.service_debut_reel', 'planningRoute.serviceStart'],
          null
        )
      )
      let end = moment(
        getNestedValue(
          item,
          ['fdr.service_fin_reel', 'planningRoute.serviceEnd'],
          null
        )
      )
      if (!start.isValid() || !end.isValid()) {
        return ''
      }
      let ms = end.diff(start)
      let d = moment.duration(ms)
      let hours = Math.floor(d.asHours())
      let rightPart = moment.utc(ms)
      if (isNaN(hours) || !rightPart.isValid()) {
        return ''
      }
      return hours + rightPart.format('[ h ]mm[ m ]ss[ s ]')
    })(),

    missionId: getNestedValue(item, ['mission.id'], null),

    //Driver (Temp reel)
    driverId: getNestedValue(
      item,
      ['driverId', 'chauffeur.id', 'driver.id'],
      null
    ),
    driverName: getNestedValue(
      item,
      ['chauffeur.nom', 'driverName', 'nom_chauffeur', 'driver.name'],
      null
    ),
    driverBadgeId: getNestedValue(
      item,
      ['chauffeur.badge_id', 'driver.badgeId'],
      null
    ),
    driverCardNumber: getNestedValue(
      item,
      ['chauffeur.numero_carte', 'driver.registrationNumber'],
      null
    ),
    driverPhone: getNestedValue(
      item,
      ['chauffeur.tel_portable', 'driver.phoneNumber'],
      null
    ),
    driverDate: getNestedValue(
      item,
      ['chauffeur.dateheure', 'driver.dateTime'],
      null
    ),
    driverDateFormatted: getNestedValueFormattedDatetimeMilliseconds(item, [
      'chauffeur.dateheure',
      'driver.dateTime',
    ]),
    driverChronoDate: getNestedValue(
      item,
      ['chauffeur.chrono_dateheure', 'driver.tachographAt'],
      null
    ),
    driverChronoEnabled: getNestedValue(
      item,
      ['chauffeur.chrono_etat', 'driver.tachographState'],
      null
    ),
    driverChronoStatus: getDriverChronoStatus(
      getNestedValue(
        item,
        ['chauffeur.chrono_imputation', 'driver.tachographImputation'],
        null
      )
    ),
    driverChornoStatusOriginal: getNestedValue(
      item,
      ['chauffeur.chrono_imputation', 'driver.tachographImputation'],
      null
    ),
    driverPlace: getNestedValue(
      item,
      ['chauffeur.site_nom', 'driver.siteName'],
      null
    ),
    sectionsNumber: getNestedValue(item, 'sectionsNumber', null, {
      allowZero: true,
    }),

    //History
    ...parseHistoryItem(item, options),

    //Mixed
    relatedPolylines,

    //@TODO: Refactor/Remove, use vehicleCategoryId instead
    //Warning: Location - List sort results by this attribute, meaning drivers/circuits will be sorted by vehicle category id (BUG!?)
    categoryId: getNestedValue(vehicleCategory, 'id', null),

    ...parseLocationItemDates(item, options),

    //Box number
    boxNumber: getNestedValue(item, 'boxAllocation.boxNumber', null),
    //Server id
    serverId: getNestedValue(item, 'boxAllocation.serverId', null),

    //@todo: Refactor and remove raw attribute
    raw: item,
  }))
}

/**
 *
 * Parse dates fields from both real-time/history
 *
 * @param {*} item
 * @param {*} options
 * @returns
 */
function parseLocationItemDates(item, options) {
  let date = getNestedValue(item, [
    'temps_reel.dateheure',
    'dh_vehicule_deb',
    'dh_exec_circuit_debut',
    'dh_activ_deb',
    'activityStart',
    'start',
    'realTime.dateTime',
  ])
  return {
    date: stringDatetimeToDate(date),
    timestamp: datetimeToTimestamp(date, 'YYYY[-]MM[-]DD HH:mm:ss'),
    dateFormatted: formatDatetimeWithSeconds(stringDatetimeToDate(date)),
    dateElapsedFormatted: (() => {
      let then = stringDatetimeToDate(date)
      let ms = moment().diff(moment(then))
      let d = moment.duration(ms)
      return (
        Math.floor(d.asHours()) + moment.utc(ms).format('[ h ]mm[ m ]ss[ s ]')
      )
    })(),

    //History mode only
    ...(options.isHistoryMode && options.selectionType === 'circuit'
      ? {
          circuitDatetimeFrom: (options.circuitDatetimeFrom =
            stringDatetimeToDate(
              getNestedValue(item, 'dh_exec_circuit_debut')
            )),
          circuitDatetimeTo: (options.circuitDatetimeTo = stringDatetimeToDate(
            getNestedValue(item, 'dh_exec_circuit_fin')
          )),
          circuitTimeFrom: getNestedValueFormattedTime(
            item,
            'dh_exec_circuit_debut'
          ),
          circuitTimeTo: getNestedValueFormattedTime(
            item,
            'dh_exec_circuit_fin'
          ),
        }
      : {}),
  }
}

export function getLocationItemLabel(item, selectionType = 'vehicle') {
  let vehicleName = getNestedValue(
    item,
    ['vehicule.nom', 'vehicule_nom', 'vehicleName', 'vehicle.name'],
    null
  )
  let driverName = getNestedValue(
    item,
    ['chauffeur.nom', 'driverName', 'driver.name'],
    null
  )
  driverName = `${driverName} (${vehicleName})`
  const labels = {
    vehicle: vehicleName,
    driver: driverName,
    circuit: getNestedValue(
      item,
      ['circuit.nom_court', 'circuit_nom_court', 'round.shortName'],
      null
    ),
  }
  return labels[selectionType]
}

/**
 * Parses item from API /v2/historique/
 *
 * - Circuit: by_circuit_dates
 * - Vehicle/Driver: by_vehicule_dates/by_chauffeur_dates
 *
 * @param {Object} item
 * @param {String} options.selectionType vehicle/driver/circuit
 * @param {Array} options.relatedPolylines
 * @returns
 */
export function parseHistoryItem(item, options = {}) {
  const isHistoryByCircuit =
    options.isHistoryMode && options.selectionType === 'circuit'
  const isHistoryByVehicleDriver =
    options.isHistoryMode &&
    ['driver', 'vehicle'].includes(options.selectionType)
  const shouldUseApiV3 = options.shouldUseApiV3

  const totalDistance = getNestedValue(
    item,
    ['distance_troncon', 'distance'],
    0
  )

  let historyByCircuitFields = !isHistoryByCircuit
    ? {}
    : {
        circuitDistance: getNestedValue(item, ['lg_realisee_circuit'], 0),
        /*circuitLeafletPolyline:
          options.relatedPolylines.length > 0
            ? options.relatedPolylines[0]
            : null,*/
      }

  let historyByVehicleOrDriverFields = !isHistoryByVehicleDriver
    ? {}
    : {
        totalDistance: shouldUseApiV3
          ? metersToKilometers(totalDistance) //Handle APIV3 reponse format
          : totalDistance,
        averageSpeed: Math.round(getNestedValue(item, ['vitesse', 'speed'], 0)),
        driverIdentification: getNestedValueFormattedTime(
          item,
          ['dh_activ_deb', 'activityStart'],
          0
        ),
        driverDesidentification: getNestedValueFormattedTime(
          item,
          ['dh_activ_fin', 'activityEnd'],
          0
        ),
        historyAddressFrom: (() => {
          let address = {
            streetAddress: getNestedValue(item, [
              'adresse_deb',
              'startAddress',
            ]),
            zipCode: getNestedValue(item, ['cp_deb', 'startZipCode']),
            city: getNestedValue(item, ['ville_deb', 'startCity']),
          }
          let r = `${address.streetAddress}`.trim()
          r = (r ? r + `, ` : r + ``) + address.zipCode
          r = (r ? r + `, ` : r + ``) + address.city
          return r
        })(),
        historyAddressTo: (() => {
          let address = {
            streetAddress: getNestedValue(item, ['adresse_fin', 'endAddress']),
            zipCode: getNestedValue(item, ['cp_fin', 'endZipCode']),
            city: getNestedValue(item, ['ville_fin', 'endCity']),
          }
          let r = `${address.streetAddress}`.trim()
          r = (r ? r + `, ` : r + ``) + address.zipCode
          r = (r ? r + `, ` : r + ``) + address.city
          return r
        })(),
        historyStartTime: getNestedValueFormattedTime(
          item,
          ['dh_activ_deb', 'activityStart'],
          0
        ),
        historyEndTime: getNestedValueFormattedTime(
          item,
          ['dh_activ_fin', 'activityEnd'],
          0
        ),

        historyStartDatetimeStamp: datetimeToTimestamp(
          getNestedValue(item, ['dh_activ_deb', 'activityStart'], 0),
          APIV2ResponseDatetimeFormat
        ),
        historyEndDatetimeStamp: datetimeToTimestamp(
          getNestedValue(item, ['dh_activ_fin', 'activityEnd'], 0),
          APIV2ResponseDatetimeFormat
        ),

        historyStartDatetime: getNestedValueFormattedDatetimeMilliseconds(
          item,
          ['dh_activ_deb', 'activityStart'],
          0
        ),

        historyEndDatetime: getNestedValueFormattedDatetimeMilliseconds(
          item,
          ['dh_activ_fin', 'activityEnd'],
          0
        ),
        totalElapsedTime: getNestedValue(item, ['duree_troncon', 'time'], 0),
        totalContactOn: getNestedValue(
          item,
          ['duree_contact', 'contactTime'],
          0
        ),
        totalBrakes: getNestedValue(item, ['duree_arret', 'stopTime'], 0),
        brakesTotalAmount: getNestedValue(item, ['nb_arret', 'stopsNumber'], 0),

        tripHistoryPolyline:
          options.relatedPolylines.length > 0
            ? options.relatedPolylines[0]
            : null,
      }

  return {
    ...(isHistoryByCircuit ? historyByCircuitFields : {}),
    ...(isHistoryByVehicleDriver ? historyByVehicleOrDriverFields : {}),
  }
}

/**
 * Parses sensor infos (Temp reel)
 * @param {*} item
 * @returns
 */
export function parseLocationItemSensors(item) {
  return {
    //Sensors ------------
    hasSensors:
      Object.keys(getNestedValue(item, 'realTime.sensors', {})).length > 0,
    //Vitesse Rotation Moteur
    sensorCanRPM: getNestedValue(item, 'realTime.sensors.can.vrm', null),
    //Fleet management system
    sensorCanKMFMS: getNestedValue(
      item,
      'realTime.sensors.can.vehicleDistance',
      null
    ),
    sensorCanFuelPerc: getNestedValue(
      item,
      'realTime.sensors.can.fuelLevel',
      null
    ),
    sensorCanConsumptionLiters: getNestedValue(
      item,
      'realTime.sensors.can.consumption',
      null
    ),
    sensorCanThrottle: getNestedValue(
      item,
      'realTime.sensors.can.accelerator',
      null
    ),
    sensorCanBatteryPerc: getNestedValue(
      item,
      'realTime.sensors.can.battery',
      null,
      {
        transform: (v) => (!isNaN(parseInt(v)) && parseInt(v) <= 4 ? null : v), //Only grab value if > 4
      }
    ),
    sensorCanBraking: getNestedValue(
      item,
      'realTime.sensors.can.brakePedal',
      null
    ),
    sensorCanClutch: getNestedValue(item, 'realTime.sensors.can.clutch', null),
    sensorTor: getNestedValue(item, 'realTime.sensors.tor', [])
      .filter((tor) => tor.state !== null)
      .map((tor) => {
        return {
          name: tor.name ? tor.name : `TOR${tor.number || '?'}`,
          enabled: tor.state === 1,
        }
      }),
    sensorAna: getNestedValue(item, 'realTime.sensors.ana', [])
      .filter((ana) => ana.value !== null)
      .map((ana) => {
        return {
          name: ana.name ? ana.name : `ANA${ana.number || '?'}`,
          value: ana.value,
        }
      }),
  }
}

/**
 *
 * Special polyline entity used by Location - History, Location - Realtime - Circuit
 * getNormalizedItem: To be able to retrieve item details from leaflet popup (Trip-history/Circuit-exec polyline popup)
 *
 * @param {*} linestring
 * @param {*} options
 * @returns
 */
export function getPolylinesFromItemLinestring(item, options = {}) {
  return linestringsToPolylines(
    [
      {
        linestring: item.linestring,
        number: generateShortId('history_'), //To be able to highlight history related polylines (Leaflet)
        //To be able to select an item when several dates are selected
        dateFormatted: formatDatetimeWithSeconds(
          adjustDateWithTimezone(item.dh_vehicule_deb||item.start||item.activityStart)
        ),
      },
    ],
    {
      ...options,
      transform(obj) {
        obj = {
          ...obj,
          ...((options && options.transform && options.transform(obj)) || {}),
        }
        return obj
      },
    }
  )
}

/**
 * @todo Move to chrono-service
 * @param {*} value
 * @returns
 */
function getDriverChronoStatus(value) {
  return (
    {
      Travail: 'working',
      Repos: 'resting',
      Conduite: 'driving',
      Dispo: 'available',
    }[value] || value
  )
}
